import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody,
} from '@parsina/framework';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { comboLabel, useShortcutHelp, useShortcutList } from '../../lib/shortcuts.js';
import { isVisitPath } from '../../constants/routes.js';

// The `?` overlay — everything the keyboard can do RIGHT NOW, and what it will
// do inside a visit.
//
// It lists the registry (src/lib/shortcuts.js), not a hand-written table: a
// shortcut that is registered is listed, one that is not registered is not, and
// there is no third state where a documented key does nothing. That is the
// whole reason screens are forbidden their own window listeners — a key nobody
// can enumerate is a key nobody discovers.
//
// It follows the mount tree, so the visit workspace's `Ctrl+S` / `Ctrl+1..4` /
// `Ctrl+Shift+E` appear while a visit is open and vanish when it closes.
//
// ── The one static section, and why it is not a contradiction ────────────
//
// core.md §7.0 says the overlay lists «the visit shortcuts (§7.5)» — and a
// student reads it BEFORE opening a visit, from the list or the record, where
// none of them are mounted. So outside a visit route the overlay appends a
// REFERENCE section, «داخل ویزیت», naming the combos the visit binds when it
// opens. It is labelled from the same `visit.shortcuts.*` keys the visit
// registers with, and it is shown only when the live list cannot contain them:
// on a visit route the registry is the truth and the reference would be a
// duplicate that could drift from it.
//
// The list below is the visit's bindings as of core.md §7.5. It is a reference,
// not a registry — pressing Ctrl+S on the patients list does nothing, and the
// heading says so.

export const VISIT_SHORTCUT_REFERENCE = [
  { combo: 'ctrl+s', labelKey: 'visit.shortcuts.save' },
  { combo: 'ctrl+1', labelKey: 'visit.shortcuts.tab1' },
  { combo: 'ctrl+2', labelKey: 'visit.shortcuts.tab2' },
  { combo: 'ctrl+3', labelKey: 'visit.shortcuts.tab3' },
  { combo: 'ctrl+4', labelKey: 'visit.shortcuts.tab4' },
  { combo: 'ctrl+enter', labelKey: 'visit.shortcuts.send' },
  { combo: 'ctrl+shift+e', labelKey: 'visit.shortcuts.end' },
];

// This component is mounted OUTSIDE the router (App.jsx renders it beside the
// gate, so it exists on the sign-in and onboarding screens too), so it cannot
// ask `useLocation`. The pathname is read when the overlay OPENS — that is
// the only moment the answer is rendered.
const onVisitRoute = () =>
  typeof window !== 'undefined' && isVisitPath(window.location?.pathname || '');

const ROW = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '6px 0',
  borderBottom: `1px solid ${C.border}`,
  fontSize: 13,
  color: C.textPrimary,
};

const KBD = {
  marginInlineStart: 'auto',
  unicodeBidi: 'isolate',
  fontSize: 11.5,
  fontWeight: 700,
  background: CT.bg,
  border: `1px solid ${C.border}`,
  borderRadius: 6,
  padding: '2px 7px',
  color: CT.ink2,
};

const HEADING = { margin: '0 0 6px', fontSize: 11.5, fontWeight: 800, color: CT.mute };

function ShortcutRow({ combo, label }) {
  return (
    <div style={ROW}>
      <span>{label}</span>
      {/* A key combination is a Latin token; it reads left-to-right inside a
          Persian layout. */}
      <kbd dir="ltr" style={KBD}>{comboLabel(combo)}</kbd>
    </div>
  );
}

export default function ShortcutHelp() {
  const { t } = useTranslation();
  const { open, setOpen } = useShortcutHelp();
  const groups = useShortcutList();

  // Decided per opening, not per render: the pathname can only change while
  // the overlay is closed (opening it is a keypress on a settled screen).
  const showReference = React.useMemo(() => (open ? !onVisitRoute() : false), [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent size="sm" data-testid="shortcut-help">
        <DialogHeader>
          <DialogTitle>{t('shortcuts.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {groups.length === 0 && !showReference && (
            <p style={{ margin: 0, fontSize: 13, color: CT.mute }}>{t('shortcuts.empty')}</p>
          )}
          {groups.map(({ group, items }) => (
            <section key={group || 'default'} style={{ marginBottom: 14 }}>
              {group && <h3 style={HEADING}>{group}</h3>}
              {items.map((item) => (
                <ShortcutRow key={item.combo} combo={item.combo} label={item.label} />
              ))}
            </section>
          ))}
          {showReference && (
            <section style={{ marginBottom: 14 }} data-testid="shortcut-help-visit-reference">
              <h3 style={HEADING}>{t('shortcuts.groups.insideVisit')}</h3>
              {VISIT_SHORTCUT_REFERENCE.map(({ combo, labelKey }) => (
                <ShortcutRow key={combo} combo={combo} label={t(labelKey)} />
              ))}
            </section>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
